import { useState } from 'react';
import { addUserPaper } from '@/lib/data';
import { fetchByDoi } from '@/lib/crossref';
import { parseBibtex, parseCitation } from '@/lib/referenceExtract';
import { humanize } from '@/lib/humanize';

// The public Pages mirror, not the private source repo — anonymous visitors
// have no access to the private one, so an issue link there would just 404
// for them. Someone with access to both repos can port issues filed here
// over to the source repo's coding spreadsheet.
const REPO = 'andreamalhera/nfdixcs_research_artifact_graph-pages';

const TRACKS = ['Engineering', 'Foundations', 'Management', 'Other'];
const RESEARCH_METHODS = [
  'formal_proof', 'survey', 'lab_experiment', 'field_experiment', 'case_study', 'interviews',
  'design_science_engineering', 'simulation', 'systematic_review', 'action_research',
  'illustrative_example', 'literature_analysis', 'none',
];
const EVALUATION_METHODS = [
  'verification', 'field_experiment', 'benchmark', 'grounded_theory', 'argumentation',
  'controlled_experiment', 'data_science', 'focus_group', 'questionnaire', 'interview',
  'motivating_example', 'technical_experiment', 'case_study', 'none',
];
const DATA_TYPES = ['real_world_data', 'synthetic_data', 'mathematical_formulae', 'undefined'];
const THREATS = ['internal_validity', 'external_validity', 'construct_validity', 'ecological_validity', 'confirmability', 'reliability'];

const EMPTY_FORM = {
  title: '',
  authors: '',
  year: '',
  track: '',
  trackIsOther: false,
  doiOrUrl: '',
  keywords: '',
  typeOfInquiry: [],
  typeOfArtifact: [],
  researchMethod: [],
  evaluationMethod: [],
  dataType: [],
  threatsToValidity: [],
  implementationExistence: false,
  implementationAvailability: false,
};

function splitList(s) {
  return s.split(',').map((v) => v.trim()).filter(Boolean);
}

function toggleMulti(list, value) {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** Single-select "toggle": picking a new value replaces the selection;
 * picking the already-active one clears it. Kept as a 0-or-1-item array so
 * the record shape matches the schema's array fields either way. */
function toggleSingle(list, value) {
  return list[0] === value ? [] : [value];
}

function buildRecord(form) {
  return {
    _userAdded: true,
    _userKey: `user-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    metadata: {
      title: form.title.trim(),
      authors: splitList(form.authors),
      doi_or_url: form.doiOrUrl.trim(),
      year: Number(form.year) || undefined,
      track: form.track,
    },
    contribution: {
      keywords: splitList(form.keywords),
      focus_intent: {
        type_of_inquiry: form.typeOfInquiry,
      },
      research_components: {},
      artifacts: [
        {
          type_of_artifact: form.typeOfArtifact,
          research_method: { methods: form.researchMethod },
          evaluation_method: form.evaluationMethod,
          data_collection: { data_type: form.dataType },
          threats_to_validity: form.threatsToValidity,
          implementation: {
            existence: form.implementationExistence,
            availability: form.implementationAvailability,
          },
        },
      ],
    },
  };
}

/** Pre-fills a "New Issue" form on the source repo with the submitted
 * paper's data, so a maintainer can review it and fold it into the real
 * dataset (methods.json / the coding spreadsheet) later — there's no
 * backend here to write it in directly. */
function buildIssueUrl(record) {
  const { metadata, contribution } = record;
  const artifact = contribution.artifacts[0];
  const body = [
    'A visitor submitted this paper through the "Add Paper" flow. Please review and, if it checks out, fold it into the coding spreadsheet / `methods.json`.',
    '',
    `**Title**: ${metadata.title}`,
    `**Authors**: ${metadata.authors.join(', ') || '_(none given)_'}`,
    `**Year**: ${metadata.year || '_(none given)_'}`,
    `**Track**: ${metadata.track || '_(none given)_'}`,
    `**DOI/URL**: ${metadata.doi_or_url || '_(none given)_'}`,
    `**Keywords**: ${contribution.keywords.join(', ') || '_(none given)_'}`,
    `**Type of Inquiry**: ${contribution.focus_intent.type_of_inquiry.join(', ') || '_(none given)_'}`,
    `**Type of Artifact**: ${artifact.type_of_artifact.join(', ') || '_(none given)_'}`,
    `**Research Method**: ${artifact.research_method.methods.join(', ') || '_(none given)_'}`,
    `**Evaluation Method**: ${artifact.evaluation_method.join(', ') || '_(none given)_'}`,
    `**Data Type**: ${artifact.data_collection.data_type.join(', ') || '_(none given)_'}`,
    `**Threats to Validity**: ${artifact.threats_to_validity.join(', ') || '_(none given)_'}`,
    `**Implementation exists**: ${artifact.implementation.existence ? 'Yes' : 'No'}`,
    `**Publicly available**: ${artifact.implementation.availability ? 'Yes' : 'No'}`,
    '',
    '<details><summary>Raw record</summary>',
    '',
    '```json',
    JSON.stringify(
      { metadata, contribution },
      null,
      2
    ),
    '```',
    '</details>',
  ].join('\n');

  const params = new URLSearchParams({
    title: `New paper submission: ${metadata.title || '(untitled)'}`,
    body,
    labels: 'paper-submission',
  });
  return `https://github.com/${REPO}/issues/new?${params.toString()}`;
}

/** ChipSelect: a labeled group of toggle chips. `selected` is always the
 * form's array field; whether clicking a chip adds/removes it (multi) or
 * replaces the whole selection (single) is up to whatever `onToggle` does. */
function ChipSelect({ label, hint, options, selected, onToggle, colorOf }) {
  return (
    <div className="add-paper__field">
      <label className="filters-panel__label">{label}</label>
      {hint && <p className="add-paper__hint">{hint}</p>}
      <div className="filters-panel__chips">
        {options.map((opt) => {
          const id = typeof opt === 'string' ? opt : opt.id;
          const text = typeof opt === 'string' ? humanize(opt) : opt.label;
          const active = selected.includes(id);
          const color = colorOf?.(id);
          return (
            <button
              key={id}
              type="button"
              className={`filter-chip ${active ? 'filter-chip--active' : ''}`}
              style={active && color ? { borderColor: color, background: `color-mix(in srgb, ${color} 15%, transparent)`, color } : undefined}
              onClick={() => onToggle(id)}
              aria-pressed={active}
            >
              {text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function AddPaperModal({ config, onClose, onAdded }) {
  const [step, setStep] = useState('input');
  const [inputMode, setInputMode] = useState('bibtex');
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [addedPaper, setAddedPaper] = useState(null);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setMultiChip = (key, value) => setForm((f) => ({ ...f, [key]: toggleMulti(f[key], value) }));
  const setSingleChip = (key, value) => setForm((f) => ({ ...f, [key]: toggleSingle(f[key], value) }));

  const handleExtract = async () => {
    if (!inputValue.trim()) {
      setError('Paste a DOI, BibTeX entry, or citation first.');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      let extracted;
      if (inputMode === 'doi') extracted = await fetchByDoi(inputValue);
      else if (inputMode === 'bibtex') extracted = parseBibtex(inputValue);
      else extracted = parseCitation(inputValue);

      setForm((f) => ({
        ...f,
        title: extracted.title || f.title,
        authors: extracted.authors?.length ? extracted.authors.join(', ') : f.authors,
        year: extracted.year ? String(extracted.year) : f.year,
        doiOrUrl: extracted.doi_or_url || f.doiOrUrl,
        keywords: extracted.keywords?.length ? extracted.keywords.join(', ') : f.keywords,
      }));
      if (inputMode === 'citation' && !extracted.title) {
        setError('Could not confidently extract fields from free-text citations — fill in the blanks below by hand.');
      }
      setStep('review');
    } catch (e) {
      setError(e.message || 'Extraction failed — you can still fill in the details manually.');
      setStep('review');
    } finally {
      setLoading(false);
    }
  };

  const missingFields = [
    !form.title.trim() && 'Title',
    !form.doiOrUrl.trim() && 'DOI / URL',
    form.typeOfInquiry.length === 0 && 'Type of Inquiry',
    form.typeOfArtifact.length === 0 && 'Type of Artifact',
  ].filter(Boolean);
  const canSubmit = missingFields.length === 0;

  const handleSubmit = async () => {
    if (!canSubmit) {
      setError(`Please fill in the required fields before submitting: ${missingFields.join(', ')}.`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const record = buildRecord(form);
      const paper = await addUserPaper(record);
      setAddedPaper(paper);
      onAdded(paper);
      setStep('success');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal" role="dialog" aria-labelledby="add-paper-title">
        <div className="modal__header">
          <h2 id="add-paper-title">Add a Paper</h2>
          <button className="paper-panel__close" onClick={onClose} aria-label="Close">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="modal__body">
          {step === 'input' && (
            <>
              <p className="add-paper__hint">
                Added papers live only in your browser (nothing is uploaded automatically) — they show up in the
                Papers list, filters, and the Graph, clearly marked as yours.
              </p>
              <div className="add-paper__tabs">
                {['bibtex', 'doi', 'citation'].map((mode) => (
                  <button
                    key={mode}
                    type="button"
                    className={`btn btn--sm ${inputMode === mode ? 'btn--primary' : 'btn--secondary'}`}
                    onClick={() => { setInputMode(mode); setError(null); }}
                  >
                    {mode === 'doi' ? 'DOI' : mode === 'bibtex' ? 'BibTeX' : 'Citation'}
                  </button>
                ))}
              </div>

              {inputMode === 'doi' ? (
                <input
                  className="toolbar__search"
                  placeholder="10.1007/978-3-031-70396-6_13 or https://doi.org/..."
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              ) : (
                <textarea
                  className="add-paper__textarea"
                  rows={6}
                  placeholder={inputMode === 'bibtex' ? '@inproceedings{key, title={...}, author={...}, year={...}, doi={...}}' : 'Paste a plain-text citation (APA, MLA, IEEE, ...)'}
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                />
              )}

              {error && <p className="add-paper__error">{error}</p>}

              <div className="modal__actions">
                <button className="btn btn--ghost" onClick={() => { setForm(EMPTY_FORM); setStep('review'); }}>
                  Skip — enter manually
                </button>
                <button className="btn btn--primary" onClick={handleExtract} disabled={loading}>
                  {loading ? 'Looking up…' : 'Extract'}
                </button>
              </div>
            </>
          )}

          {step === 'review' && (
            <>
              <p className="add-paper__hint">Verify the extracted fields, and fill in whatever's missing.</p>
              {error && <p className="add-paper__error">{error}</p>}

              <div className="add-paper__grid">
                <div className="add-paper__field">
                  <label className="filters-panel__label">Title *</label>
                  <input className="toolbar__search" value={form.title} onChange={(e) => setField('title', e.target.value)} />
                </div>
                <div className="add-paper__field">
                  <label className="filters-panel__label">Authors (comma-separated)</label>
                  <input className="toolbar__search" value={form.authors} onChange={(e) => setField('authors', e.target.value)} />
                </div>
                <div className="add-paper__field">
                  <label className="filters-panel__label">Year</label>
                  <input className="toolbar__search" type="number" value={form.year} onChange={(e) => setField('year', e.target.value)} />
                </div>
                <div className="add-paper__field">
                  <label className="filters-panel__label">DOI / URL *</label>
                  <input className="toolbar__search" value={form.doiOrUrl} onChange={(e) => setField('doiOrUrl', e.target.value)} />
                </div>
                <div className="add-paper__field">
                  <label className="filters-panel__label">Keywords (comma-separated)</label>
                  <input className="toolbar__search" value={form.keywords} onChange={(e) => setField('keywords', e.target.value)} />
                </div>
              </div>

              <ChipSelect
                label="Track"
                options={TRACKS}
                selected={form.trackIsOther ? ['Other'] : form.track ? [form.track] : []}
                onToggle={(id) => {
                  if (id === 'Other') setForm((f) => ({ ...f, trackIsOther: true, track: '' }));
                  else setForm((f) => ({ ...f, trackIsOther: false, track: f.track === id ? '' : id }));
                }}
              />
              {form.trackIsOther && (
                <input
                  className="toolbar__search"
                  placeholder="Enter the track name"
                  value={form.track}
                  onChange={(e) => setField('track', e.target.value)}
                />
              )}

              <p className="add-paper__hint">
                These can't be extracted from a citation — pick what applies. Type of Inquiry and Type of Artifact
                (single choice, required) drive how this paper is classified and clustered.
              </p>

              <ChipSelect
                label="Type of Inquiry * (pick one)"
                options={config.typesOfInquiry || []}
                selected={form.typeOfInquiry}
                onToggle={(id) => setSingleChip('typeOfInquiry', id)}
                colorOf={(id) => config.typesOfInquiry?.find((t) => t.id === id)?.color}
              />
              <ChipSelect
                label="Type of Artifact * (pick one)"
                options={config.typesOfArtifact || []}
                selected={form.typeOfArtifact}
                onToggle={(id) => setSingleChip('typeOfArtifact', id)}
                colorOf={(id) => config.typesOfArtifact?.find((t) => t.id === id)?.color}
              />
              <ChipSelect label="Research Method" options={RESEARCH_METHODS} selected={form.researchMethod} onToggle={(id) => setMultiChip('researchMethod', id)} />
              <ChipSelect label="Evaluation Method" options={EVALUATION_METHODS} selected={form.evaluationMethod} onToggle={(id) => setMultiChip('evaluationMethod', id)} />
              <ChipSelect label="Data Type" options={DATA_TYPES} selected={form.dataType} onToggle={(id) => setMultiChip('dataType', id)} />
              <ChipSelect label="Threats to Validity" options={THREATS} selected={form.threatsToValidity} onToggle={(id) => setMultiChip('threatsToValidity', id)} />

              <div className="add-paper__toggles">
                <label className="graph-controls__toggle">
                  <input type="checkbox" checked={form.implementationExistence} onChange={(e) => setField('implementationExistence', e.target.checked)} />
                  <span>Implementation exists</span>
                </label>
                <label className="graph-controls__toggle">
                  <input type="checkbox" checked={form.implementationAvailability} onChange={(e) => setField('implementationAvailability', e.target.checked)} />
                  <span>Publicly available</span>
                </label>
              </div>

              <p className="add-paper__note">
                This quick-add covers one artifact per paper — enough to place it correctly in the filters, Graph,
                and Landscape. It won't have the full multi-artifact coding depth of the rest of the corpus.
              </p>

              <div className="modal__actions">
                <button className="btn btn--ghost" onClick={() => setStep('input')}>← Back</button>
                <button className="btn btn--primary" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? 'Adding…' : 'Add Paper'}
                </button>
              </div>
            </>
          )}

          {step === 'success' && addedPaper && (
            <>
              <p className="add-paper__hint">
                Added to your browser. You'll find it pinned at the top of the papers list on the right, and
                marked with a dashed ring in the visualizations and graph.
              </p>
              <p className="add-paper__note">
                It's only in your browser, though. Nobody else sees it, and it's not part of the shared dataset.
                If you'd like it considered for the real corpus, open an issue with the details pre-filled. It
                only takes one click, and it would make us happy. A maintainer can review and fold it into the
                coding spreadsheet.
              </p>
              <div className="modal__actions">
                <button className="btn btn--ghost" onClick={onClose}>Done</button>
                <a
                  className="btn btn--primary"
                  href={buildIssueUrl(addedPaper)}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={onClose}
                >
                  Propose for shared dataset →
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
}
